import { SpanStatusCode } from '@opentelemetry/api';
import {
  EmeraldItemNotFound,
  ItemNotFound,
  LinkedItemHandlingRequired,
  WicTenderRestriction,
} from 'src/common/helpers/error-code';
import { createSpan, endSpan } from 'src/common/helpers/tracing-helper';
import { IItemLookup } from 'src/common/interfaces/dtos/IItemLookup';
import { IItemInputData } from 'src/common/interfaces/IItemInputData';
import { TransactionActions } from 'src/common/redux/reducers/transaction-reducer';
import { beginPaymentSession, inPaymentSession } from 'src/devices/payment/pinpad-operations';
import { updateCartStatus } from 'src/home-page/business-logic/update-cart-status';
import {
  handleEmeraldItemNotFound,
  handleItemNotFound,
} from 'src/item-not-found/business-logic/handle-item-not-found';
import { returnItem } from 'src/return-services-display/business-logic/service/return-item';
import { showEwicErrorModal } from 'src/tendering/business-logic/handle-ewic-error-handling';
import { store } from '../../common/redux/configure-store';
import { MiddleAreaActions } from '../../common/redux/reducers/middle-area-reducer';
import { applyCartResponse } from '../../home-page/business-logic/common/apply-cart-response';
import { hasCardData, swipeAhead } from './../../devices/payment/get-card-details';
import { addLookupLineItem } from './add-lookup-line-item';
import { handlableErrors, handleLineItemError } from './handle-line-item-error';
import { queryAndBuildLineItem } from './query-and-build-line-item';
import { addItem } from './service/add-item';
import { CartCustomerData } from './service/interfaces/cart-response';
import { LineItemAddDto } from './service/interfaces/line-item-add.dto';

export interface ILineItemAddData {
  inputData: IItemInputData;
  itemLookup: IItemLookup;
  lineItem: LineItemAddDto;
}

export const lineItemAdd = async (lineItemData: ILineItemAddData): Promise<void> => {
  const { isTransactionTotaled } = store.getState().transaction;
  const { posMode } = store.getState();
  const isReturn = !!posMode.returnMode.returnType;
  lineItemData.lineItem.returnType = posMode.returnMode.returnType;
  // add a tm flag
  lineItemData.lineItem.isTrainingMode = posMode.trainingMode;
  // addIMEIToLineItem(lineItemToAdd);
  // AddSelectedPriceSourceToLineItem(lineItemToAdd);

  const { lineItem, inputData } = lineItemData;

  const span = createSpan('line-item-add');

  if (isTransactionTotaled) {
    await updateCartStatus();
  }

  const itemCallback = isReturn ? returnItem : addItem;
  return itemCallback(lineItem)
    .then(async cartResponse => {
      await applyCartResponse(cartResponse);

      if (cartResponse.customer) {
        applyCustomerPoints(cartResponse.customer);
      }

      store.dispatch(MiddleAreaActions.showActionWindow());

      //TODO: Check the store value for allow swipe head or not https://jira.ncr.com/browse/XLR-1718
      if (cartResponse.cartInfo?.totals?.balanceDue) {
        checkPinpadAndSwipeAhead();
      }
    })
    .catch(async err => {
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: 'itemCallback error',
      });
      if (handlableErrors.includes(err.code)) {
        if (lineItem.withData) {
          const action = isReturn ? 'Return' : 'Sell';
          lineItemData = await queryAndBuildLineItem({ inputData, action });
        }
        return handleLineItemError(lineItemData, err);
      }

      switch (err.code) {
        case ItemNotFound:
          return handleItemNotFound(inputData);
        case EmeraldItemNotFound:
          return handleEmeraldItemNotFound();
        case WicTenderRestriction: {
          err.message = isReturn ? 'EwicReturnItem' : 'EwicAddItem';
          return showEwicErrorModal(err);
        }
        case LinkedItemHandlingRequired:
          return addLookupLineItem({ inputData, itemLookup: err.data.itemLookups[0] });
        default:
          throw err;
      }
    })
    .finally(() => endSpan(span));
};

const applyCustomerPoints = (customer: CartCustomerData): void => {
  const programBalances = customer.programBalances;
  customer.points =
    programBalances?.length === 0
      ? undefined
      : programBalances?.reduce((a, b) => a + (b.earnedPoints || 0), 0);

  const currentPoints = store.getState().transaction.transactionCustomer?.points;
  if (customer.points !== currentPoints) {
    store.dispatch(TransactionActions.setCustomer(customer));
  }
};

const checkPinpadAndSwipeAhead = async (): Promise<void> => {
  if (store.getState().pinPad.isPinpadConfigured && !hasCardData()) {
    if (!inPaymentSession()) {
      await beginPaymentSession();
    }
    swipeAhead(true);
  }
};
